import React, { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { Loader2, Target, Trash2, CheckCircle2, Plus, Sparkles } from "lucide-react";

export default function LifeHub() {
  const { user } = useAuth();
  const [tools, setTools] = useState([]);
  const [goals, setGoals] = useState([]);
  const [newGoal, setNewGoal] = useState({ title: "", area: "life" });
  const [running, setRunning] = useState(null);
  const [inputs, setInputs] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/life-tools").then(r => setTools(r.data));
    if (user) api.get("/goals").then(r => setGoals(r.data));
  }, [user]);

  const addGoal = async (e) => {
    e.preventDefault();
    if (!newGoal.title) return;
    const r = await api.post("/goals", newGoal);
    setGoals([r.data, ...goals]);
    setNewGoal({ title: "", area: "life" });
  };

  const toggleGoal = async (g) => {
    await api.patch(`/goals/${g.id}`, { done: !g.done, progress: !g.done ? 100 : 0 });
    setGoals(goals.map(x => x.id === g.id ? { ...x, done: !x.done, progress: !x.done ? 100 : 0 } : x));
  };

  const delGoal = async (id) => { await api.delete(`/goals/${id}`); setGoals(goals.filter(g => g.id !== id)); };

  const runTool = async () => {
    if (!running) return;
    setLoading(true); setResult(null);
    try {
      const r = await api.post("/life-tools/run", { tool_slug: running.slug, inputs });
      setResult(r.data.result);
    } finally { setLoading(false); }
  };

  return (
    <Layout hideRightPanel>
      <PageHero
        testId="lifehub-hero"
        eyebrow="Life Hub"
        title={<>Live <span className="italic">better.</span> Every day.</>}
        sub="A curated set of daily-life tools: planner, decision helper, wellness check-ins, learning paths. All powered by GPT-5.4."
      />

      <section className="mb-10">
        <h2 className="font-display text-2xl font-semibold mb-4">Daily life tools</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger" data-testid="life-tools-grid">
          {tools.map(t => (
            <div key={t.slug} className="curio-card p-5" data-testid={`life-tool-${t.slug}`}>
              <div className="text-[10px] uppercase tracking-widest text-[#5B6478] font-bold">{t.area}</div>
              <h3 className="font-display text-2xl font-semibold mt-1 mb-2">{t.name}</h3>
              <p className="text-sm text-[#0B1220]/80 mb-4">{t.desc}</p>
              <button onClick={()=>{ setRunning(t); setResult(null); setInputs({}); }} className="btn-coral text-sm w-full justify-center">
                <Sparkles size={13}/> {t.cta}
              </button>
            </div>
          ))}
        </div>
      </section>

      {running && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={()=>setRunning(null)} data-testid="life-tool-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
            <div className="text-[10px] uppercase tracking-widest text-[#F97316] font-bold">{running.area}</div>
            <h3 className="font-display text-3xl font-bold mb-2">{running.name}</h3>
            <p className="text-sm text-[#5B6478] mb-4">{running.desc}</p>
            <textarea value={inputs.text || ""} onChange={e=>setInputs({...inputs, text: e.target.value})} rows={4} placeholder="Give it context…" className="w-full bg-[#F7F9FC] border border-[#E3E8F0] rounded-xl px-3 py-2 text-sm mb-3 focus:outline-none focus:border-[#0B1220]" data-testid="life-tool-input"/>
            <button onClick={runTool} disabled={loading} className="btn-coral w-full justify-center mb-3" data-testid="life-tool-run">
              {loading ? <><Loader2 size={13} className="animate-spin"/> Working…</> : <><Sparkles size={13}/> Run</>}
            </button>
            {result && <div className="bg-[#F7F9FC] border border-[#E3E8F0] rounded-xl p-4 text-sm whitespace-pre-wrap leading-relaxed" data-testid="life-tool-result">{result}</div>}
            <button onClick={()=>setRunning(null)} className="btn-ghost w-full justify-center text-sm mt-3">Close</button>
          </div>
        </div>
      )}

      {user ? (
        <section>
          <h2 className="font-display text-2xl font-semibold mb-4 flex items-center gap-2"><Target size={20}/> Your goals</h2>
          <form onSubmit={addGoal} className="flex gap-2 mb-4" data-testid="add-goal-form">
            <input value={newGoal.title} onChange={e=>setNewGoal({...newGoal, title: e.target.value})} placeholder="e.g., Ship v1 of my app by March 15" className="flex-1 bg-[#F7F9FC] border border-[#E3E8F0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#0B1220]" data-testid="goal-title-input"/>
            <select value={newGoal.area} onChange={e=>setNewGoal({...newGoal, area: e.target.value})} className="bg-[#F7F9FC] border border-[#E3E8F0] rounded-xl px-3 py-2 text-sm">
              {["life","wellness","learning","career"].map(a=><option key={a}>{a}</option>)}
            </select>
            <button className="btn-coral text-sm" data-testid="add-goal-btn"><Plus size={13}/> Add</button>
          </form>
          <div className="space-y-2" data-testid="goals-list">
            {goals.length === 0 ? <p className="text-sm text-[#5B6478]">No goals yet. Add your first one above.</p> : goals.map(g => (
              <div key={g.id} className="curio-card p-4 flex items-center gap-3">
                <button onClick={()=>toggleGoal(g)} className="flex-shrink-0" data-testid={`goal-toggle-${g.id}`}>
                  <CheckCircle2 size={22} className={g.done ? "text-[#F97316]" : "text-[#E3E8F0]"} fill={g.done ? "#F97316" : "none"}/>
                </button>
                <div className="flex-1">
                  <div className={`font-medium ${g.done ? 'line-through text-[#5B6478]' : ''}`}>{g.title}</div>
                  <div className="text-xs text-[#5B6478] uppercase tracking-widest">{g.area}</div>
                </div>
                <button onClick={()=>delGoal(g.id)} className="text-[#5B6478] hover:text-rose-500 p-1"><Trash2 size={14}/></button>
              </div>
            ))}
          </div>
        </section>
      ) : (
        <section className="curio-gradient text-white rounded-2xl p-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-2">Set goals. Track wins.</h2>
          <p className="text-white/70 mb-4">Sign in to save your goals and unlock streaks.</p>
          <Link to="/login" className="btn-coral inline-flex">Sign in</Link>
        </section>
      )}
    </Layout>
  );
}
